"use client";

import {
  GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY,
  GUEST_SPATIUS_AVATAR_VARIANTS,
  type GuestSpatiusAvatarExposure,
  type GuestSpatiusAvatarVariant,
  parseGuestSpatiusAvatarVariant,
  recordGuestSpatiusAvatarExposure,
} from "@/lib/experiments/guestSpatiusAvatar";
import { useEffect, useState } from "react";

type FeatureFlagLoadContext = { errorsLoading?: boolean };

export type GuestSpatiusAvatarPostHogClient = {
  getFeatureFlagResult: (
    key: string,
    options?: { fresh?: boolean; send_event?: boolean }
  ) => { variant?: string } | undefined;
  onFeatureFlags: (
    callback: (
      flags: string[],
      variants: Record<string, string | boolean>,
      context?: FeatureFlagLoadContext
    ) => void
  ) => () => void;
  reloadFeatureFlags: () => void;
};

export type GuestSpatiusAvatarExperimentResolution = {
  status: "loading" | "resolved";
  variant: GuestSpatiusAvatarVariant;
  source: "pending" | "posthog" | "fallback" | "bypassed";
  reason?: "disabled" | "invalid_variant" | "load_error" | "timeout" | "unavailable";
};

const LOADING: GuestSpatiusAvatarExperimentResolution = {
  status: "loading",
  variant: GUEST_SPATIUS_AVATAR_VARIANTS.control,
  source: "pending",
};

function fallback(
  reason: NonNullable<GuestSpatiusAvatarExperimentResolution["reason"]>,
  source: "fallback" | "bypassed" = "fallback"
): GuestSpatiusAvatarExperimentResolution {
  return {
    status: "resolved",
    variant: GUEST_SPATIUS_AVATAR_VARIANTS.control,
    source,
    reason,
  };
}

export function useGuestSpatiusAvatarExperiment({
  enabled,
  posthog,
  timeoutMs = 1_500,
}: {
  enabled: boolean;
  posthog: GuestSpatiusAvatarPostHogClient | null | undefined;
  timeoutMs?: number;
}): GuestSpatiusAvatarExperimentResolution {
  const [resolution, setResolution] = useState<GuestSpatiusAvatarExperimentResolution>(() =>
    enabled ? LOADING : fallback("disabled", "bypassed")
  );

  useEffect(() => {
    if (!enabled) {
      setResolution(fallback("disabled", "bypassed"));
      return;
    }
    if (!posthog) {
      setResolution(fallback("unavailable"));
      return;
    }

    let settled = false;
    let reloadRequested = false;
    let unsubscribe: () => void = () => undefined;
    setResolution(LOADING);

    const finish = (next: GuestSpatiusAvatarExperimentResolution) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      setResolution(next);
    };
    const timeoutId = window.setTimeout(() => finish(fallback("timeout")), timeoutMs);

    try {
      unsubscribe = posthog.onFeatureFlags((_flags, _variants, context) => {
        if (settled || !reloadRequested) return;
        if (context?.errorsLoading) {
          finish(fallback("load_error"));
          return;
        }

        try {
          const result = posthog.getFeatureFlagResult(GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY, {
            fresh: true,
            send_event: false,
          });
          const variant = parseGuestSpatiusAvatarVariant(result?.variant);
          if (!variant) {
            finish(fallback("invalid_variant"));
            return;
          }
          finish({ status: "resolved", variant, source: "posthog" });
        } catch {
          finish(fallback("unavailable"));
        }
      });
      reloadRequested = true;
      posthog.reloadFeatureFlags();
    } catch {
      finish(fallback("unavailable"));
    }

    return () => {
      settled = true;
      window.clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [enabled, posthog, timeoutMs]);

  return resolution;
}

export function exposeGuestSpatiusAvatarExperiment(
  posthog: GuestSpatiusAvatarPostHogClient,
  renderedVariant: GuestSpatiusAvatarVariant
): GuestSpatiusAvatarExposure | null {
  try {
    const current = posthog.getFeatureFlagResult(GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY, {
      fresh: true,
      send_event: false,
    });
    if (parseGuestSpatiusAvatarVariant(current?.variant) !== renderedVariant) return null;

    posthog.getFeatureFlagResult(GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY, {
      fresh: true,
      send_event: true,
    });
    return recordGuestSpatiusAvatarExposure({ variant: renderedVariant });
  } catch {
    return null;
  }
}
